import React, { useRef, useState } from "react";
import { Building2, History, ChevronDown } from "lucide-react";
import Organogram from "../Components/Organogram";
import { useLanguage } from '../context/LanguageContext';

const tlImages = {
    pre1900:         { src: '/Images/1990.jpeg',             caption: 'Rural Tamil Nadu, pre-1900' },
    period1895:      { src: null,                           caption: 'Madras Presidency, 1895–1897' },
    period1904_1912: { src: null,                           caption: 'Cooperative Credit Act era, 1904–1912' },
    year1904:        { src: '/Images/1904.jpeg',             caption: 'Tirur Village Cooperative Society, 1904' },
    period1920s:     { src: '/Images/1940.jpeg',            caption: 'Cooperative growth, 1920s–1940s' },
    postIndependence:{ src: '/Images/postind.jpeg',         caption: 'Post-Independence cooperative expansion' },
    socialReform:    { src: null,                           caption: 'Cooperatives and social reform' },
};

const TimelineSection = ({ t }) => {
    const entries = Object.entries(t.timeline);
    const [revealed, setRevealed] = useState(1);
    const entryRefs = useRef([]);

    const revealNext = () => {
        const next = revealed;
        setRevealed(prev => prev + 1);
        setTimeout(() => {
            entryRefs.current[next]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 80);
    };


    return (
        <div className="mb-16">
            <div className="text-center mb-12">
                <h3 className="text-4xl font-bold bg-gradient-to-r from-[#005A8D] to-[#0077B6] bg-clip-text text-transparent mb-4">
                    {t.timelineTitle}
                </h3>
                <div className="w-32 h-1 bg-gradient-to-r from-[#005A8D] to-[#0077B6] mx-auto rounded-full"></div>
            </div>

            <div className="relative">
                <div
                    className="absolute left-1/2 -translate-x-1/2 top-0 w-0.5 bg-gradient-to-b from-[#005A8D] via-amber-400 to-[#0077B6] hidden md:block transition-all duration-700"
                    style={{ height: `${(revealed / entries.length) * 100}%` }}
                />

                <div className="flex flex-col gap-10">
                    {entries.map(([key, item], index) => {
                        const isVisible = index < revealed;
                        const isHighlight = key === 'year1904';
                        const isLeft = index % 2 === 0;
                        const isLast = index === entries.length - 1;
                        const isLastRevealed = index === revealed - 1;
                        const img = tlImages[key];

                        return (
                            <div key={key} ref={el => entryRefs.current[index] = el} className={isVisible ? '' : 'hidden'}>
                                <div
                                    className={`relative flex flex-col md:flex-row items-center gap-6 transition-all duration-700 ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                                    style={{
                                        opacity: isVisible ? 1 : 0,
                                        transform: isVisible ? 'scale(1)' : 'scale(0.85)',
                                    }}
                                >
                                    {/* Image + caption */}
                                    <div className="w-full md:w-5/12">
                                        {img.src ? (
                                            <>
                                                <img
                                                    src={img.src}
                                                    alt={item.title}
                                                    className="w-full object-contain rounded-t-2xl shadow-lg"
                                                />
                                                <div className="bg-gray-800/80 text-white text-xs text-center py-1.5 px-3 rounded-b-2xl italic">
                                                    {img.caption}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="w-full h-48 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 text-sm italic shadow-inner">
                                                Image coming soon
                                            </div>
                                        )}
                                    </div>

                                    {/* Centre dot */}
                                    <div className="hidden md:flex md:w-2/12 flex-col items-center gap-1">
                                        <div className="relative flex items-center justify-center">
                                            {/* Pulsing ring — only on last revealed clickable dot */}
                                            {isLastRevealed && !isLast && (
                                                <span className="absolute w-12 h-12 rounded-full bg-[#005A8D]/20 animate-ping" />
                                            )}
                                            <button
                                                onClick={isLastRevealed && !isLast ? revealNext : undefined}
                                                className={`relative w-6 h-6 rounded-full border-2 border-white z-10 flex items-center justify-center shadow-md transition-transform ${
                                                    isLastRevealed && !isLast ? 'cursor-pointer hover:scale-125 scale-110' : 'cursor-default'
                                                } ${isHighlight ? 'bg-gradient-to-br from-amber-400 to-amber-600' : 'bg-gradient-to-br from-[#005A8D] to-[#0077B6]'}`}
                                            >
                                                {isLastRevealed && !isLast
                                                    ? <ChevronDown className="w-3 h-3 text-white" />
                                                    : isHighlight && <span className="text-white text-[8px]">★</span>
                                                }
                                            </button>
                                        </div>
                                    </div>

                                    {/* Card */}
                                    <div className="w-full md:w-5/12">
                                        <div className={`rounded-xl shadow-lg p-6 ${
                                            isHighlight
                                                ? 'bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-400'
                                                : 'bg-white border-l-4 border-[#005A8D]'
                                        }`}>
                                            <h4 className={`text-base font-bold mb-3 ${isHighlight ? 'text-amber-700' : 'text-[#005A8D]'}`}>
                                                {item.title}
                                            </h4>
                                            <ul className="space-y-2">
                                                {item.points.map((point, idx) => (
                                                    <li key={idx} className="flex items-start">
                                                        <span className={`mt-1.5 mr-2 w-1.5 h-1.5 rounded-full flex-shrink-0 ${isHighlight ? 'bg-amber-500' : 'bg-[#0077B6]'}`} />
                                                        <span className={`text-sm leading-relaxed ${isHighlight ? 'text-gray-800 font-medium' : 'text-gray-700'}`}>
                                                            {point}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    {/* Mobile: tap dot below card */}
                                    {isLastRevealed && !isLast && (
                                        <div className="md:hidden flex flex-col items-center w-full mt-4 gap-1">
                                            <div className="relative flex items-center justify-center">
                                                <span className="absolute w-10 h-10 rounded-full bg-[#005A8D]/20 animate-ping" />
                                                <button
                                                    onClick={revealNext}
                                                    className="relative w-6 h-6 rounded-full bg-gradient-to-br from-[#005A8D] to-[#0077B6] border-2 border-white shadow-md z-10 flex items-center justify-center"
                                                >
                                                    <ChevronDown className="w-3 h-3 text-white" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const AboutUsOption1 = () => {
    const { language } = useLanguage();

    const content = {
        en: {
            pageTitle: "About Us",
            pageSubtitle: "Pioneering Cooperative Excellence in Tamil Nadu",
            historyTitle: "History of the Cooperative Movement in Tamil Nadu",
            historyText1: "Tamil Nadu is the birthplace of India's cooperative movement. Long before institutional credit systems existed, farmers depended on landlords and moneylenders, often falling into deep debt due to recurring famines. In response to this agrarian distress, the cooperative model emerged as a people-centred solution to provide fair, organised, and legal access to credit.",
            historyText2: "From the early 20th century, Tamil Nadu laid the foundation for a cooperative system that has since evolved into one of the strongest and most diverse cooperative ecosystems in the country.",
            timelineTitle: "Key Milestones – A Timeline",
            adminStructure: "Administrative Structure",
            adminRoles: {
                secretary: "Secretary to Government",
                secretarySubtitle: "Cooperation, Food & Consumer Protection",
                registrar: "Registrar of Cooperative Societies",
                additionalRegistrar: "Additional Registrar",
                jointRegistrars: "Joint Registrars",
                deputyRegistrars: "Deputy Registrars",
                subRegistrars: "Cooperative Sub-Registrars",
                seniorInspectors: "Senior Inspectors",
                juniorInspectors: "Junior Inspectors"
            },
            timeline: {
                pre1900: {
                    title: "Pre-1900",
                    points: [
                        "Farmers relied on private moneylenders with high interest rates",
                        "Repeated famines worsened rural indebtedness",
                        "Early relief laws failed to address structural credit issues"
                    ]
                },
                period1895: {
                    title: "1895–1897",
                    points: [
                        "The Madras Presidency commissioned Sir Frederick Augustus Nicholson to study agricultural credit systems",
                        "Nicholson recommended cooperative credit societies on the German Raiffeisen model",
                        "The Famine Commission of 1901 reinforced these recommendations"
                    ]
                },
                period1904_1912: {
                    title: "1904–1912",
                    points: [
                        "The Cooperative Credit Societies Act, 1904 was enacted",
                        "The Act was strengthened through amendments in 1912"
                    ]
                },
                year1904: {
                    title: "1904",
                    points: [
                        "On 30 August 1904, the Tirur Village Cooperative Society was registered in Tiruvallur district",
                        "This became the first cooperative society in India, marking Tamil Nadu's leadership in the movement"
                    ]
                },
                period1920s: {
                    title: "1920s–1940s",
                    points: [
                        "Cooperative institutions spread across the Madras Presidency",
                        "Leaders such as P.S. Kumarasamy Raja, Pt. Thiyagaraya Chettiyar, and P.T. Rajan strengthened cooperative banking and governance",
                        "Mahatma Gandhi engaged with cooperative institutions in Tamil Nadu, reinforcing the philosophy of collective self-reliance"
                    ]
                },
                postIndependence: {
                    title: "Post-Independence",
                    points: [
                        "Cooperative institutions were integrated into India's Five-Year Plans",
                        "Jawaharlal Nehru emphasized cooperatives as instruments of economic democracy",
                        "Cooperative Week began to be observed nationwide"
                    ]
                },
                socialReform: {
                    title: "Social Reform",
                    points: [
                        "Leaders like Thanthai Periyar advocated cooperatives as tools for social equality",
                        "Cooperatives actively worked to reduce caste barriers and promote inclusive membership",
                        "Special societies supported marginalized communities, artisans, and workers"
                    ]
                }
            }
        },
        ta: {
            pageTitle: "எங்களைப் பற்றி",
            pageSubtitle: "தமிழ்நாட்டில் கூட்டுறவு சிறப்புக்கு முன்னோடி",
            historyTitle: "தமிழ்நாட்டில் கூட்டுறவு இயக்கத்தின் வரலாறு",
            historyText1: "தமிழ்நாடு இந்தியாவின் கூட்டுறவு இயக்கத்தின் பிறப்பிடமாகும். நிறுவன கடன் அமைப்புகள் தோன்றுவதற்கு முன்பே, விவசாயிகள் நில உடைமையாளர்கள் மற்றும் கடன் வழங்குபவர்களை நம்பியிருந்தனர், மீண்டும் மீண்டும் ஏற்படும் பஞ்சங்களால் ஆழ்ந்த கடனில் சிக்கினர். இந்த விவசாய துயரத்திற்கு பதிலளிக்கும் விதமாக, நியாயமான, ஒழுங்கமைக்கப்பட்ட மற்றும் சட்டபூர்வமான கடன் அணுகலை வழங்க கூட்டுறவு மாதிரி மக்கள் சார்ந்த தீர்வாக உருவானது.",
            historyText2: "20ஆம் நூற்றாண்டின் தொடக்கத்திலிருந்து, தமிழ்நாடு ஒரு கூட்டுறவு அமைப்புக்கான அடித்தளத்தை அமைத்தது, இது நாட்டின் வலிமையான மற்றும் மிகவும் பன்முக கூட்டுறவு சூழல் அமைப்புகளில் ஒன்றாக உருவாகியுள்ளது.",
            timelineTitle: "முக்கிய மைல்கற்கள் – ஒரு காலவரிசை",
            adminStructure: "நிர்வாக அமைப்பு",
            adminRoles: {
                secretary: "அரசுச் செயலாளர்",
                secretarySubtitle: "கூட்டுறவு, உணவு மற்றும் நுகர்வோர் பாதுகாப்பு",
                registrar: "கூட்டுறவுச் சங்கங்கள் பதிவாளர்",
                additionalRegistrar: "கூடுதல் பதிவாளர்",
                jointRegistrars: "கூட்டு பதிவாளர்கள்",
                deputyRegistrars: "துணை பதிவாளர்கள்",
                subRegistrars: "கூட்டுறவு துணை பதிவாளர்கள்",
                seniorInspectors: "மூத்த ஆய்வாளர்கள்",
                juniorInspectors: "இளநிலை ஆய்வாளர்கள்"
            },
            timeline: {
                pre1900: {
                    title: "1900க்கு முன்",
                    points: [
                        "விவசாயிகள் அதிக வட்டி விகிதங்களுடன் தனியார் கடன் வழங்குபவர்களை நம்பினர்",
                        "மீண்டும் மீண்டும் ஏற்படும் பஞ்சங்கள் கிராமப்புற கடன்பாட்டை மோசமாக்கியது",
                        "ஆரம்பகால நிவாரண சட்டங்கள் கட்டமைப்பு கடன் சிக்கல்களை தீர்க்க தவறிவிட்டன"
                    ]
                },
                period1895: {
                    title: "1895–1897",
                    points: [
                        "மெட்ராஸ் பிரசிடென்சி விவசாய கடன் அமைப்புகளை ஆய்வு செய்ய சர் ஃபிரடெரிக் அகஸ்டஸ் நிக்கல்சனை நியமித்தது",
                        "ஜெர்மன் ரைஃபைசென் மாதிரியின் அடிப்படையில் கூட்டுறவு கடன் சங்கங்களை நிக்கல்சன் பரிந்துரைத்தார்",
                        "1901 பஞ்ச ஆணையம் இந்த பரிந்துரைகளை வலுப்படுத்தியது"
                    ]
                },
                period1904_1912: {
                    title: "1904–1912",
                    points: [
                        "கூட்டுறவு கடன் சங்கங்கள் சட்டம், 1904 இயற்றப்பட்டது",
                        "1912 இல் திருத்தங்கள் மூலம் சட்டம் வலுப்படுத்தப்பட்டது"
                    ]
                },
                year1904: {
                    title: "1904",
                    points: [
                        "ஆகஸ்ட் 30, 1904 அன்று, திருவள்ளூர் மாவட்டத்தில் திரூர் கிராம கூட்டுறவு சங்கம் பதிவு செய்யப்பட்டது",
                        "இது இந்தியாவின் முதல் கூட்டுறவு சங்கமாக மாறியது, இயக்கத்தில் தமிழ்நாட்டின் தலைமையை குறிக்கிறது"
                    ]
                },
                period1920s: {
                    title: "1920கள்–1940கள்",
                    points: [
                        "மெட்ராஸ் பிரசிடென்சி முழுவதும் கூட்டுறவு நிறுவனங்கள் பரவின",
                        "பி.எஸ். குமாரசாமி ராஜா, பிட்டி தியாகராயர் மற்றும் பி.டி. ராஜன் போன்ற தலைவர்கள் கூட்டுறவு வங்கி மற்றும் நிர்வாகத்தை வலுப்படுத்தினர்",
                        "மகாத்மா காந்தி தமிழ்நாட்டில் கூட்டுறவு நிறுவனங்களுடன் ஈடுபட்டார், கூட்டு சுய நம்பிக்கையின் தத்துவத்தை வலுப்படுத்தினார்"
                    ]
                },
                postIndependence: {
                    title: "சுதந்திரத்திற்குப் பின்",
                    points: [
                        "கூட்டுறவு நிறுவனங்கள் இந்தியாவின் ஐந்தாண்டு திட்டங்களில் ஒருங்கிணைக்கப்பட்டன",
                        "ஜவஹர்லால் நேரு பொருளாதார ஜனநாயகத்தின் கருவிகளாக கூட்டுறவுகளை வலியுறுத்தினார்",
                        "கூட்டுறவு வாரம் நாடு முழுவதும் கடைபிடிக்கப்பட்டது"
                    ]
                },
                socialReform: {
                    title: "சமூக சீர்திருத்தம்",
                    points: [
                        "தந்தை பெரியார் போன்ற தலைவர்கள் சமூக சமத்துவத்திற்கான கருவிகளாக கூட்டுறவுகளை ஆதரித்தனர்",
                        "கூட்டுறவுகள் சாதி தடைகளை குறைக்கவும் உள்ளடக்கிய உறுப்பினர்களை ஊக்குவிக்கவும் தீவிரமாக பணியாற்றின",
                        "சிறப்பு சங்கங்கள் ஓரங்கட்டப்பட்ட சமூகங்கள், கைவினைஞர்கள் மற்றும் தொழிலாளர்களுக்கு ஆதரவளித்தன"
                    ]
                }
            }
        }
    };

    const t = content[language];

    return (
        <div
            className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50"
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
        >
            {/* Hero Section */}
            <div className="relative bg-gradient-to-r from-[#003d5c] via-[#005A8D] to-[#0077B6] py-10 mt-4 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
                </div>
                <div className="relative max-w-6xl mx-auto px-6 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-3 tracking-tight">
                        {t.pageTitle}
                    </h1>
                    <div className="w-24 h-1.5 bg-gradient-to-r from-transparent via-white to-transparent mx-auto mb-4"></div>
                    <p className="text-lg text-white/95 max-w-3xl mx-auto font-light leading-relaxed">
                        {t.pageSubtitle}
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-12">

                {/* History Section */}
                <div className="relative mb-16">
                    <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#005A8D] via-[#0077B6] to-transparent rounded-full"></div>

                    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-100">
                        <div className="bg-gradient-to-r from-[#005A8D] to-[#0077B6] p-8">
                            <div className="flex items-center gap-4">
                                <div className="p-4 bg-white/20 backdrop-blur-sm rounded-xl">
                                    <History className="w-8 h-8 text-white" />
                                </div>
                                <h2 className="text-3xl md:text-4xl font-bold text-white">{t.historyTitle}</h2>
                            </div>
                        </div>

                        <div className="p-8 md:p-12 bg-gradient-to-br from-slate-50 to-white">
                            <div className="prose prose-lg max-w-none">
                                <p className="text-gray-700 text-lg leading-relaxed mb-6 first-letter:text-5xl first-letter:font-bold first-letter:text-[#005A8D] first-letter:mr-2 first-letter:float-left">
                                    {t.historyText1}
                                </p>
                                <p className="text-gray-700 text-lg leading-relaxed">
                                    {t.historyText2}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Timeline Section */}
                <TimelineSection t={t} />


                {/* Administrative Structure — full width */}
                <div className="mb-16">
                    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-100">
                        <div className="bg-gradient-to-r from-[#005A8D] to-[#0077B6] py-6 px-8">
                            <h2 className="text-2xl font-bold text-white text-center flex items-center justify-center gap-3">
                                <Building2 className="w-7 h-7" />
                                {t.adminStructure}
                            </h2>
                        </div>

                        <div className="p-8 bg-gradient-to-br from-slate-50 to-white">
                            <div className="flex flex-col space-y-3 max-w-md mx-auto">
                                {[
                                    { title: t.adminRoles.secretary, subtitle: t.adminRoles.secretarySubtitle },
                                    { title: t.adminRoles.registrar, subtitle: "" },
                                    { title: t.adminRoles.additionalRegistrar, subtitle: "" },
                                    { title: t.adminRoles.jointRegistrars, subtitle: "" },
                                    { title: t.adminRoles.deputyRegistrars, subtitle: "" },
                                    { title: t.adminRoles.subRegistrars, subtitle: "" },
                                    { title: t.adminRoles.seniorInspectors, subtitle: "" },
                                    { title: t.adminRoles.juniorInspectors, subtitle: "" },
                                ].map((item, index, arr) => (
                                    <React.Fragment key={index}>
                                        <div className="w-full group/card">
                                            <div className="relative bg-white rounded-lg shadow-md border-l-4 border-[#005A8D] px-5 py-4 transition-all duration-300 hover:shadow-xl hover:border-l-8 hover:bg-gradient-to-r hover:from-[#005A8D] hover:to-[#0077B6] hover:text-white cursor-pointer">
                                                <p className="text-center text-gray-800 font-semibold text-sm group-hover/card:text-white transition-colors">
                                                    {item.title}
                                                </p>
                                                {item.subtitle && (
                                                    <p className="text-center text-xs text-gray-600 mt-1 group-hover/card:text-white/90 transition-colors">
                                                        {item.subtitle}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {index !== arr.length - 1 && (
                                            <div className="flex justify-center">
                                                <div className="w-0.5 h-4 bg-gradient-to-b from-[#005A8D] to-[#0077B6]"></div>
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* Organogram */}
            <Organogram />
        </div>
    );
};

export default AboutUsOption1;